import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import {
  CheckIcon,
  SparklesIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  ArrowTopRightOnSquareIcon,
  GiftIcon,
  RocketLaunchIcon,
  CpuChipIcon,
  BuildingOffice2Icon,
} from '@heroicons/react/24/outline';
import {
  useSubscription,
  useSubscriptionPlans,
  useCreateCheckout,
  useCreateBillingPortal,
  useCancelSubscription,
  useResumeSubscription,
  useVerifyCheckout,
} from '@/hooks/useApi';
import toast from 'react-hot-toast';
import CheckoutModal from './CheckoutModal';
import PageSkeleton from '@/components/PageSkeleton';

const FALLBACK_PLANS = [
  {
    id: 'free' as const,
    name: 'Free',
    monthlyPrice: 0,
    yearlyPrice: 0,
    maxInvoices: 5,
    maxClients: 5,
    isPaid: false,
    features: ['5 invoices / month', '5 clients', 'Basic reports', 'Email support'],
  },
  {
    id: 'starter' as const,
    name: 'Starter',
    monthlyPrice: 1490,
    yearlyPrice: 14900,
    maxInvoices: 50,
    maxClients: 25,
    isPaid: true,
    features: ['50 invoices / month', '25 clients', 'VAT reports', 'Bank sync', 'Recurring invoices'],
  },
  {
    id: 'professional' as const,
    name: 'Professional',
    monthlyPrice: 2990,
    yearlyPrice: 29900,
    maxInvoices: null,
    maxClients: null,
    isPaid: true,
    features: ['Unlimited invoices', 'Unlimited clients', 'AI estimates', 'Priority support', 'Custom branding', 'API access'],
  },
  {
    id: 'enterprise' as const,
    name: 'Enterprise',
    monthlyPrice: 5990,
    yearlyPrice: 59900,
    maxInvoices: null,
    maxClients: null,
    isPaid: true,
    features: ['Everything in Pro', 'Multi-organization', 'Custom integrations', 'Dedicated account manager', 'SLA guarantee', 'SSO / SAML'],
  },
];

export default function SubscriptionPage() {
  const { t } = useTranslation();
  const [interval, setInterval] = useState<'monthly' | 'yearly'>('monthly');
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: subscription, isLoading, isError: isSubscriptionError, refetch } = useSubscription();
  const { data: plans, isLoading: isLoadingPlans, isError: isPlansError } = useSubscriptionPlans();
  const checkout = useCreateCheckout();
  const billingPortal = useCreateBillingPortal();
  const cancelSub = useCancelSubscription();
  const resumeSub = useResumeSubscription();
  const verifyCheckout = useVerifyCheckout();
  const verifiedRef = useRef(false);
  const [checkoutSecret, setCheckoutSecret] = useState<string | null>(null);

  // Verify checkout session when returning from Stripe
  useEffect(() => {
    const status = searchParams.get('status');
    const sessionId = searchParams.get('session_id');

    if (status === 'success' && sessionId && !verifiedRef.current) {
      verifiedRef.current = true;
      verifyCheckout.mutate(sessionId, {
        onSuccess: () => {
          toast.success(t('subscription.paymentSuccess', 'Subscription activated successfully!'));
          refetch();
          setSearchParams({}, { replace: true });
        },
        onError: () => {
          toast.error(t('subscription.paymentError', 'Failed to verify payment. Please refresh.'));
          setSearchParams({}, { replace: true });
        },
      });
    } else if (status === 'cancelled') {
      toast.error(t('subscription.paymentCancelled', 'Payment was cancelled.'));
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  if (isLoading || isLoadingPlans) return <PageSkeleton variant="cards" />;

  const currentPlan = subscription?.plan || 'free';
  const isActive = subscription?.status === 'active' || subscription?.status === 'trialing';
  const isCanceled = subscription?.status === 'canceled';
  const isOnGracePeriod = isCanceled && subscription?.currentPeriodEnd && new Date(subscription.currentPeriodEnd) > new Date();

  const handleCheckout = (planId: string) => {
    checkout.mutate(
      { plan: planId, interval },
      {
        onSuccess: (data) => {
          setCheckoutSecret(data.clientSecret);
        },
      }
    );
  };

  const handleCheckoutComplete = () => {
    setCheckoutSecret(null);
    toast.success(t('subscription.paymentSuccess', 'Subscription activated successfully!'));
    refetch();
  };

  const handleBillingPortal = () => {
    billingPortal.mutate(undefined, {
      onSuccess: (data) => {
        window.location.href = data.portalUrl;
      },
    });
  };

  const formatPrice = (cents: number) => {
    const euros = cents / 100;
    return euros % 1 === 0 ? euros.toFixed(0) : euros.toFixed(2).replace('.', ',');
  };

  const planVisuals: Record<string, { icon: typeof GiftIcon; bgColor: string; iconColor: string }> = {
    free: {
      icon: GiftIcon,
      bgColor: 'bg-emerald-100',
      iconColor: 'text-emerald-600',
    },
    starter: {
      icon: RocketLaunchIcon,
      bgColor: 'bg-sky-100',
      iconColor: 'text-sky-600',
    },
    professional: {
      icon: CpuChipIcon,
      bgColor: 'bg-violet-100',
      iconColor: 'text-violet-600',
    },
    enterprise: {
      icon: BuildingOffice2Icon,
      bgColor: 'bg-amber-100',
      iconColor: 'text-amber-700',
    },
  };

  const usageItems = [
    {
      key: 'clients',
      used: subscription?.usedClients ?? 0,
      limit: subscription?.maxClients ?? null,
      remaining: subscription?.remainingClients ?? null,
      label: t('subscription.usage.clients'),
    },
    {
      key: 'invoices',
      used: subscription?.usedInvoicesThisMonth ?? 0,
      limit: subscription?.maxInvoices ?? null,
      remaining: subscription?.remainingInvoicesThisMonth ?? null,
      label: t('subscription.usage.invoicesThisMonth'),
    },
  ];
  const planList = plans && plans.length > 0 ? plans : FALLBACK_PLANS;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header — compact with inline toggle */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">{t('subscription.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{t('subscription.subtitle')}</p>
        </div>
        {/* Interval toggle */}
        <div className="relative bg-gray-100 p-0.5 rounded-lg inline-flex items-center self-start sm:self-auto">
          <button
            onClick={() => setInterval('monthly')}
            className={`relative z-10 px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
              interval === 'monthly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('subscription.monthly')}
          </button>
          <button
            onClick={() => setInterval('yearly')}
            className={`relative z-10 px-4 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
              interval === 'yearly'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t('subscription.yearly')}
          </button>
          {interval === 'yearly' && (
            <span className="absolute -top-2 -right-3 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-px rounded-full">
              -17%
            </span>
          )}
        </div>
      </div>

      {(isSubscriptionError || isPlansError) && (
        <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {t('subscription.partialDataWarning', 'Some subscription data could not be loaded. Showing available plan information.')}
        </div>
      )}

      {/* Current plan / Grace period banners — compact */}
      {subscription && isActive && (
        <div className={`mb-5 rounded-lg px-4 py-3 border ${
          currentPlan === 'free'
            ? 'bg-gray-50/80 border-gray-200'
            : 'bg-primary-50/60 border-primary-100'
        }`}>
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${
                  currentPlan === 'free' ? 'bg-gray-400' : 'bg-primary-600'
                }`}>
                  {currentPlan === 'free' ? (
                    <CheckCircleIcon className="w-4 h-4 text-white" />
                  ) : (
                    <SparklesIcon className="w-4 h-4 text-white" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {t('subscription.currentPlan')}: {t(`subscription.plans.${currentPlan}`)}
                    <span className={`ml-2 text-xs font-medium ${currentPlan === 'free' ? 'text-gray-400' : 'text-primary-500'}`}>
                      {t(`subscription.statuses.${subscription.status}`)}
                    </span>
                  </p>
                  {subscription.currentPeriodEnd && (
                    <p className="text-xs text-gray-400">
                      {t('subscription.renewsOn')} {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              {currentPlan !== 'free' && (
                <button onClick={handleBillingPortal} className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap ml-3">
                  {t('subscription.manageBilling')}
                  <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {usageItems.map((item) => (
                <div key={item.key} className="rounded-md border border-gray-200 bg-white/80 px-3 py-2">
                  <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">{item.label}</p>
                  <p className="text-sm font-semibold text-gray-900 mt-0.5">
                    {item.limit === null
                      ? `${item.used} · ${t('subscription.usage.unlimited')}`
                      : t('subscription.usage.usedOfLimit', { used: item.used, limit: item.limit })}
                  </p>
                  {item.limit !== null && (
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {t('subscription.usage.remaining', { remaining: item.remaining ?? 0 })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {isOnGracePeriod && (
        <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">{t('subscription.canceledTitle')}</p>
            <p className="text-xs text-amber-600 mt-0.5">
              {t('subscription.canceledMessage', { date: new Date(subscription!.currentPeriodEnd!).toLocaleDateString() })}
            </p>
          </div>
          <button onClick={() => resumeSub.mutate()} disabled={resumeSub.isPending} className="btn-primary text-xs px-3 py-1.5 ml-3">
            {t('subscription.resume')}
          </button>
        </div>
      )}

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {planList.map((plan) => {
          const isCurrent = currentPlan === plan.id;
          const price = interval === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
          const isPopular = plan.id === 'professional';
          const planVisual = planVisuals[plan.id] ?? planVisuals.free;
          const PlanIcon = planVisual.icon;

          return (
            <div
              key={plan.id}
              className={`relative group rounded-xl border transition-all duration-200 ${
                isCurrent
                  ? 'border-primary-300 bg-primary-50/40 ring-2 ring-primary-500/20'
                  : isPopular
                  ? 'border-primary-200 bg-white shadow-md shadow-primary-100/40'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }`}
            >
              {/* Popular ribbon */}
              {isPopular && !isCurrent && (
                <div className="absolute -top-2.5 inset-x-0 flex justify-center">
                  <span className="inline-flex items-center gap-1 bg-primary-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full shadow">
                    <SparklesIcon className="w-3 h-3" />
                    {t('subscription.popular')}
                  </span>
                </div>
              )}
              {isCurrent && (
                <div className="absolute -top-2.5 inset-x-0 flex justify-center">
                  <span className="inline-flex items-center gap-1 bg-primary-600 text-white text-[10px] font-bold px-3 py-0.5 rounded-full shadow">
                    <CheckCircleIcon className="w-3 h-3" />
                    {t('subscription.currentPlanBtn')}
                  </span>
                </div>
              )}

              <div className={`p-5 flex flex-col h-full ${isPopular || isCurrent ? 'pt-6' : ''}`}>
                {/* Plan header */}
                <div className="mb-4">
                  <div className="flex items-start gap-2.5">
                    <span className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${planVisual.bgColor}`}>
                      <PlanIcon className={`w-3.5 h-3.5 ${planVisual.iconColor}`} />
                    </span>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">{t(`subscription.plans.${plan.id}`)}</h3>
                      <p className="text-[11px] text-gray-400 mt-0.5">{t(`subscription.planDescriptions.${plan.id}`)}</p>
                    </div>
                  </div>
                </div>

                {/* Price block */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-0.5">
                    <span className="text-3xl font-extrabold text-gray-900 tracking-tight">
                      €{formatPrice(price)}
                    </span>
                    {plan.isPaid && (
                      <span className="text-xs text-gray-400 font-medium ml-0.5">
                        /{interval === 'monthly' ? t('subscription.mo') : t('subscription.yr')}
                      </span>
                    )}
                  </div>
                  {plan.id === 'free' && (
                    <p className="text-[11px] text-gray-400 mt-0.5">{t('subscription.freeForever')}</p>
                  )}
                  {plan.isPaid && interval === 'yearly' && (
                    <p className="text-[11px] text-emerald-600 font-medium mt-0.5">
                      €{formatPrice(plan.monthlyPrice)}/{t('subscription.mo')} billed yearly
                    </p>
                  )}
                </div>

                {/* Divider */}
                <div className="h-px bg-gray-100 mb-4" />

                {/* Features */}
                <ul className="space-y-2 mb-5 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckIcon className={`w-3.5 h-3.5 flex-shrink-0 stroke-[2.5] ${
                        isPopular ? 'text-primary-500' : 'text-emerald-500'
                      }`} />
                      <span className="text-xs text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                {plan.id === 'free' ? (
                  <button
                    disabled
                    className="w-full py-2 text-xs font-semibold text-gray-400 bg-gray-50 rounded-lg cursor-not-allowed border border-gray-100"
                  >
                    {isCurrent ? t('subscription.currentPlanBtn') : t('subscription.freeForever')}
                  </button>
                ) : isCurrent && isActive ? (
                  <div className="space-y-1.5">
                    <button
                      disabled
                      className="w-full py-2 text-xs font-semibold text-primary-600 bg-primary-50 rounded-lg cursor-not-allowed border border-primary-100"
                    >
                      ✓ {t('subscription.currentPlanBtn')}
                    </button>
                    <button
                      onClick={() => cancelSub.mutate()}
                      disabled={cancelSub.isPending}
                      className="w-full py-1.5 text-[11px] text-gray-400 hover:text-red-500 transition-colors"
                    >
                      {t('subscription.cancel')}
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleCheckout(plan.id)}
                    disabled={checkout.isPending}
                    className={`w-full py-2 text-xs font-semibold rounded-lg transition-all duration-150 disabled:opacity-50 ${
                      isPopular
                        ? 'bg-primary-600 text-white hover:bg-primary-700 active:scale-[0.98]'
                        : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.98]'
                    }`}
                  >
                    {checkout.isPending ? '...' : t('subscription.subscribe')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-center gap-1.5 mt-6 pb-4">
        <ShieldCheckIcon className="w-3.5 h-3.5 text-gray-300" />
        <p className="text-[11px] text-gray-400">
          SSL security · GDPR compliance · Cancel anytime
        </p>
      </div>

      {/* Stripe Checkout Modal */}
      {checkoutSecret && (
        <CheckoutModal
          clientSecret={checkoutSecret}
          onClose={() => setCheckoutSecret(null)}
          onComplete={handleCheckoutComplete}
        />
      )}
    </div>
  );
}
